type Cell = string | number

function triggerDownload(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const csvCell = (v: Cell) => {
  const s = String(v ?? '')
  return /[";\n\r]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s
}

export function downloadCsv(filename: string, headers: string[], rows: Cell[][]) {
  const body = [headers, ...rows].map((r) => r.map(csvCell).join(';')).join('\r\n')
  triggerDownload(filename, new Blob([`\uFEFF${body}`], { type: 'text/csv;charset=utf-8' }))
}

export function todayIso() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const fileSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

export const fileKind = (name: string) => (name.split('.').pop() ?? 'PDF').toUpperCase().slice(0, 4)

export function dmyToIso(dmy: string) {
  const [d, m, y] = dmy.split('.')
  return d && m && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : dmy
}

export function downloadIcs(filename: string, ev: { title: string; date: string; description?: string }) {
  const day = ev.date.replaceAll('-', '')
  const next = new Date(`${ev.date}T00:00:00`)
  next.setDate(next.getDate() + 1)
  const end = `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}${String(next.getDate()).padStart(2, '0')}`
  const esc = (s: string) => s.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n')
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Urunver//Control Plane//TR',
    'BEGIN:VEVENT',
    `UID:${day}-${Math.random().toString(36).slice(2)}@urunver`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
    `DTSTART;VALUE=DATE:${day}`,
    `DTEND;VALUE=DATE:${end}`,
    `SUMMARY:${esc(ev.title)}`,
    ev.description ? `DESCRIPTION:${esc(ev.description)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n')
  triggerDownload(filename, new Blob([ics], { type: 'text/calendar;charset=utf-8' }))
}

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** rows: etiket/değer satırları; table: çok sütunlu liste (sayısal sütunlar sağa yaslanır). */
export type PrintSection = { heading?: string; rows?: [string, string][]; total?: [string, string]; table?: { head: string[]; rows: string[][]; numeric?: number[]; foot?: string[] } }

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c)

function gridTable(t: NonNullable<PrintSection['table']>) {
  const cls = (i: number) => (t.numeric?.includes(i) ? ' class="num"' : '')
  const head = `<tr>${t.head.map((h, i) => `<th${cls(i)}>${esc(h)}</th>`).join('')}</tr>`
  const body = t.rows.map((r) => `<tr>${r.map((c, i) => `<td${cls(i)}>${esc(c)}</td>`).join('')}</tr>`).join('')
  const foot = t.foot ? `<tr class="total">${t.foot.map((c, i) => `<td${cls(i)}>${esc(c)}</td>`).join('')}</tr>` : ''
  return `<table class="grid"><thead>${head}</thead><tbody>${body}${foot}</tbody></table>`
}

export function openPrintDocument(doc: { title: string; subtitle?: string; sections: PrintSection[]; note?: string }) {
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  document.body.appendChild(frame)
  const win = frame.contentWindow
  if (!win) {
    frame.remove()
    return false
  }
  const sections = doc.sections
    .map(
      (s) => `<section>${s.heading ? `<h2>${esc(s.heading)}</h2>` : ''}${
        s.table ? gridTable(s.table) : ''
      }${s.rows?.length || s.total ? `<table>${(s.rows ?? [])
        .map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`)
        .join('')}${s.total ? `<tr class="total"><th>${esc(s.total[0])}</th><td>${esc(s.total[1])}</td></tr>` : ''}</table>` : ''}</section>`,
    )
    .join('')
  const css = getComputedStyle(document.documentElement)
  const token = (name: string) => css.getPropertyValue(name).trim()
  win.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(doc.title)}</title><style>
:root{--text:${token('--print-text')};--muted:${token('--print-muted')};--faint:${token('--print-faint')};--line:${token('--print-line')};--accent:${token('--print-accent')}}
body{font-family:Inter,system-ui,sans-serif;color:var(--text);margin:40px;font-size:13px}
header{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid var(--accent);padding-bottom:14px;margin-bottom:24px}
header strong{font-size:18px;color:var(--accent)}h1{font-size:20px;margin:0 0 4px}p.sub{margin:0;color:var(--muted)}
h2{font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:var(--muted);margin:22px 0 8px}
table{width:100%;border-collapse:collapse}th,td{padding:8px 0;border-bottom:1px solid var(--line);text-align:left}
td{text-align:right;font-variant-numeric:tabular-nums}tr.total th,tr.total td{font-weight:700;border-bottom:2px solid var(--text)}
table.grid th,table.grid td{text-align:left;padding:6px 8px 6px 0;font-size:12px}table.grid th{color:var(--muted);font-weight:600}
table.grid .num{text-align:right;font-variant-numeric:tabular-nums}table.grid thead{display:table-header-group}table.grid tr{break-inside:avoid}
footer{margin-top:32px;color:var(--faint);font-size:11px}@media print{body{margin:16mm}}
</style></head><body><header><div><h1>${esc(doc.title)}</h1>${doc.subtitle ? `<p class="sub">${esc(doc.subtitle)}</p>` : ''}</div><strong>Ürünver</strong></header>${sections}<footer>${esc(doc.note ?? '')} · ${new Date().toLocaleString('tr-TR')}</footer></body></html>`)
  win.document.close()
  win.addEventListener('afterprint', () => frame.remove(), { once: true })
  setTimeout(() => {
    win.focus()
    win.print()
  }, 300)
  return true
}
